import { Platform } from 'react-native';
import * as FileSystem from 'expo-file-system/legacy';
import * as Sharing from 'expo-sharing';
import { getOrganization, getCurrentOrgID } from '@/data/organization';
import { getImageSource } from '@/utils/FileManager';
import { deriveFlowOffsets, formatDurationLabel, formatOffsetLabel, looksLikeHtml } from '@/utils/serviceFlow';

/**
 * ServiceFlowExporter - Exports event Service Flow as a formatted PDF
 * Formats the document as an "Order of Service" with event metadata and tasks
 */
class ServiceFlowExporter {
    /**
     * Export Service Flow to PDF
     * @param {Object} eventInstance - The event instance data
     * @param {Object} options - Additional options
     * @returns {Promise<boolean>} Success status
     */
    static async exportToPDF(eventInstance, options = {}) {
        try {
            if (!eventInstance) {
                throw new Error('Event instance is required');
            }

            const sortedTasks = this._getSortedTasks(eventInstance);
            const branding = await this._getBranding(options);
            const organizationLogoDataUri = branding?.organizationLogoUrl
                ? await this._tryGetDataUriFromRemoteOrStorageUrl({ uri: branding.organizationLogoUrl })
                : null;
            const assigneeAvatarDataByPersonId = await this._getAssigneeAvatarDataByPersonId(sortedTasks);

            const htmlContent = this._generateHTML(eventInstance, sortedTasks, {
                ...options,
                organizationName: branding?.organizationName,
                organizationLogoDataUri,
                assigneeAvatarDataByPersonId,
            });

            if (Platform.OS === 'web') {
                return await this._exportToPDFWeb(htmlContent, options);
            }

            return await this._exportToPDFNative(htmlContent, eventInstance, options);
        } catch (error) {
            console.error('Service Flow PDF Export Error:', error);
            throw error;
        }
    }

    static async _getBranding(options) {
        if (options?.organizationName || options?.organizationLogoUrl) {
            return {
                organizationName: options?.organizationName,
                organizationLogoUrl: options?.organizationLogoUrl,
            };
        }

        try {
            const orgID = await getCurrentOrgID();
            if (!orgID) {
                return { organizationName: null, organizationLogoUrl: null };
            }

            const org = await getOrganization(orgID);
            return {
                organizationName: org?.name || null,
                organizationLogoUrl: org?.logoPublicUrlLongDark || null,
            };
        } catch {
            return { organizationName: null, organizationLogoUrl: null };
        }
    }

    static async _getAssigneeAvatarDataByPersonId(sortedTasks) {
        const assignees = (sortedTasks || []).flatMap(t => t?.Assignees || []);
        const uniqueAssigneesById = new Map();
        assignees.forEach(a => {
            if (a?.id && !uniqueAssigneesById.has(a.id)) {
                uniqueAssigneesById.set(a.id, a);
            }
        });

        const results = {};

        for (const [personId, assignee] of uniqueAssigneesById.entries()) {
            try {
                if (!assignee?.avatarStorageKey && !assignee?.uri) {
                    continue;
                }

                const source = assignee?.uri
                    ? { uri: assignee.uri }
                    : await getImageSource(
                        assignee.avatarStorageKey,
                        assignee.avatarUpdatedAt ? `${assignee.id}+${assignee.avatarUpdatedAt}` : undefined,
                    );

                const dataUri = await this._tryGetDataUriFromRemoteOrStorageUrl(source);
                if (dataUri) {
                    results[personId] = dataUri;
                }
            } catch {
                // ignore individual avatar failures
            }
        }

        return results;
    }

    static async _tryGetDataUriFromRemoteOrStorageUrl(source) {
        if (!source?.uri) return null;

        try {
            if (Platform.OS === 'web') {
                return await this._getWebDataUri(source);
            }

            return await this._getNativeDataUri(source);
        } catch {
            return null;
        }
    }

    static async _getWebDataUri(source) {
        const response = await fetch(source.uri, { headers: source.headers || undefined });
        if (!response.ok) return null;

        const contentType = response.headers.get('content-type') || 'image/jpeg';
        const blob = await response.blob();

        const dataUri = await new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = () => resolve(reader.result);
            reader.onerror = () => reject(reader.error || new Error('Could not read export image'));
            reader.onabort = () => reject(new Error('Export image read canceled'));
            reader.readAsDataURL(blob);
        });

        if (typeof dataUri !== 'string') return null;
        if (!dataUri.startsWith('data:')) return null;

        // Ensure the mime is the actual content type
        if (dataUri.startsWith('data:application/octet-stream')) {
            const base64 = dataUri.split(',')[1];
            return `data:${contentType};base64,${base64}`;
        }

        return dataUri;
    }

    static async _getNativeDataUri(source) {
        const fileUri = `${FileSystem.cacheDirectory || FileSystem.documentDirectory}sf_export_${Date.now()}_${Math.random().toString(16).slice(2)}.img`;
        try {
            const download = await FileSystem.downloadAsync(source.uri, fileUri, {
                headers: source.headers || undefined,
            });
            const base64 = await FileSystem.readAsStringAsync(download.uri, {
                encoding: FileSystem.EncodingType.Base64,
            });
            const mime = this._guessImageMimeType(source.uri) || 'image/jpeg';
            return `data:${mime};base64,${base64}`;
        } finally {
            try {
                await FileSystem.deleteAsync(fileUri, { idempotent: true });
            } catch {
                // A failed export should still attempt to remove its temp file.
            }
        }
    }

    static _guessImageMimeType(uri) {
        const clean = (uri || '').split('?')[0].toLowerCase();
        if (clean.endsWith('.png')) return 'image/png';
        if (clean.endsWith('.webp')) return 'image/webp';
        if (clean.endsWith('.gif')) return 'image/gif';
        if (clean.endsWith('.jpeg') || clean.endsWith('.jpg')) return 'image/jpeg';
        return null;
    }

    /**
     * Get sorted service flow items from ServiceFlowInstances (by position).
     * @param {Object} eventInstance - The event instance (with ServiceFlowInstances relation)
     * @returns {Array} Sorted service flow items
     */
    static _getSortedTasks(eventInstance) {
        const items = eventInstance?.ServiceFlowInstances || [];
        const derivedOffsets = deriveFlowOffsets(items);

        return items
            .map(item => ({
                ...item,
                _derivedOffset: derivedOffsets.get(item.id),
                // Typed item discriminant — legacy rows infer TASK from a
                // linked TaskInstance; AGENDA/WORSHIP never get fake assignees.
                type: item.type || (item.TaskInstance || item.taskInstanceID ? 'TASK' : 'AGENDA'),
                // Flatten for the HTML renderer
                title: item.title || item.TaskInstance?.Task?.title || item.Task?.title || 'Untitled Item',
                description: item.description || null,
                time: item.time || null,
                durationMinutes: item.durationMinutes ?? null,
                Assignees: item.TaskInstance?.Assignees || [],
                additionalDetails: item.description || null,
                Task: item.TaskInstance?.Task || item.Task || null,
            }))
            .sort((a, b) => (a.position ?? 0) - (b.position ?? 0));
    }

    /**
     * Format assignees for display
     * @param {Array} assignees - Array of assignee objects
     * @returns {string} Formatted assignee string
     */
    static _formatAssignees(assignees) {
        if (!assignees || assignees.length === 0) {
            return '<span class="unassigned">Unassigned</span>';
        }

        return assignees
            .sort((a, b) => (a.position || 0) - (b.position || 0))
            .map(a => `${a.firstName || ''} ${a.lastName || ''}`.trim())
            .filter(name => name)
            .join(', ');
    }

    /**
     * Format a "HH:MM" clock string to "h:mm A" for display.
     * Falls back to ISO/date parsing for legacy values.
     * @param {string} timeString - "HH:MM" or ISO time string
     * @returns {string} Formatted time
     */
    static _formatTime(timeString) {
        if (timeString === null || timeString === undefined || timeString === '') return '';

        if (typeof timeString === 'string') {
            const clockMatch = timeString.trim().match(/^(\d{1,2}):(\d{2})(?::(\d{2})(?:\.\d+)?)?$/);
            if (clockMatch) {
                const hours = Number(clockMatch[1]);
                const minutes = Number(clockMatch[2]);
                const seconds = Number(clockMatch[3] || 0);
                if (hours > 23 || minutes > 59 || seconds > 59) return '';

                const ampm = hours >= 12 ? 'PM' : 'AM';
                const h12 = ((hours + 11) % 12) + 1;
                return `${h12}:${minutes.toString().padStart(2, '0')} ${ampm}`;
            }
        }

        const date = this._toValidDate(timeString);
        if (!date) return '';

        return date.toLocaleString('en-US', {
            hour: 'numeric',
            minute: '2-digit',
            hour12: true
        });
    }

    /**
     * Format an instant in the ORGANIZATION timezone (spec §7.1) — never the
     * device timezone. Falls back to the legacy formatter when no tz given.
     */
    static _formatTimeInZone(value, timezone) {
        const date = this._toValidDate(value);
        if (!date) return '';
        try {
            return date.toLocaleString('en-US', {
                hour: 'numeric',
                minute: '2-digit',
                hour12: true,
                ...(timezone ? { timeZone: timezone } : {}),
            });
        } catch {
            return this._formatTime(value);
        }
    }

    /**
     * Per-item gutter time, matching the timeline: v2 items show their time
     * only when showTime is set — explicit offsets win, otherwise the
     * sequentially derived start. Rows predating the showTime contract (NULL
     * / absent field) keep the legacy authored-clock behavior.
     */
    static _formatItemTime(task, eventInstance, timezone) {
        const allDay = eventInstance?.allDay ?? eventInstance?.Event?.defaultAllDay;
        const isV2 = task?.showTime !== undefined && task?.showTime !== null;
        const offset = task?.startOffsetMinutes ?? task?._derivedOffset;

        if (allDay) {
            if (isV2) return task.showTime && offset != null ? formatOffsetLabel(offset) : '';
            return task?.startOffsetMinutes == null ? '' : formatOffsetLabel(task.startOffsetMinutes);
        }

        if (isV2 && !task.showTime) return '';
        if (task?.resolvedStartTime) {
            const label = this._formatTimeInZone(task.resolvedStartTime, timezone);
            if (label) return label;
        }
        if (offset !== null && offset !== undefined && (!isV2 || task.showTime)) {
            const start = this._toValidDate(eventInstance?.startTime);
            if (start) {
                return this._formatTimeInZone(new Date(start.getTime() + offset * 60000), timezone);
            }
        }
        return isV2 ? '' : this._formatTime(task?.time);
    }

    static _toValidDate(value) {
        if (value === null || value === undefined || value === '') return null;

        if (value instanceof Date) {
            return Number.isNaN(value.getTime()) ? null : value;
        }

        let normalizedValue = value;
        if (typeof value === 'string') {
            const trimmedValue = value.trim();
            if (!trimmedValue || /^\d{1,2}:\d{2}(?::\d{2}(?:\.\d+)?)?$/.test(trimmedValue)) return null;

            if (/^\d{10}$/.test(trimmedValue)) {
                normalizedValue = Number(trimmedValue) * 1000;
            } else if (/^\d{13}$/.test(trimmedValue)) {
                normalizedValue = Number(trimmedValue);
            } else {
                const dateOnlyMatch = trimmedValue.match(/^(\d{4})-(\d{2})-(\d{2})$/);
                if (dateOnlyMatch) {
                    const year = Number(dateOnlyMatch[1]);
                    const month = Number(dateOnlyMatch[2]);
                    const day = Number(dateOnlyMatch[3]);
                    const dateOnly = new Date(year, month - 1, day);
                    const isValidDateOnly = dateOnly.getFullYear() === year
                        && dateOnly.getMonth() === month - 1
                        && dateOnly.getDate() === day;
                    return isValidDateOnly ? dateOnly : null;
                }
                normalizedValue = trimmedValue.replace(/^(\d{4}-\d{2}-\d{2})\s+(\d{2}:\d{2})/, '$1T$2');
            }
        } else if (typeof value === 'number' && Number.isFinite(value) && Math.abs(value) < 100000000000) {
            normalizedValue = value * 1000;
        }

        try {
            const date = new Date(normalizedValue);
            return Number.isNaN(date.getTime()) ? null : date;
        } catch {
            return null;
        }
    }

    /**
     * Format date for display
     * @param {string} dateString - ISO date string
     * @returns {string} Formatted date
     */
    static _formatDate(dateString) {
        const date = this._toValidDate(dateString);
        if (!date) return '';

        return date.toLocaleString('en-US', {
            weekday: 'long',
            year: 'numeric',
            month: 'long',
            day: 'numeric'
        });
    }

    /**
     * Format date and time range
     * @param {Object} eventInstance - Event instance
     * @returns {string} Formatted date/time range
     */
    static _formatDateTime(eventInstance, timezone = null) {
        const tz = timezone ? { timeZone: timezone } : {};
        const inZone = (value, opts) => {
            const date = this._toValidDate(value);
            if (!date) return '';
            try { return date.toLocaleString('en-US', { ...opts, ...tz }); } catch { return date.toLocaleString('en-US', opts); }
        };
        const startTime = [eventInstance?.startTime, eventInstance?.Event?.defaultStartTime]
            .find(value => this._toValidDate(value) || this._formatTime(value));
        const endTime = [eventInstance?.endTime, eventInstance?.Event?.defaultEndTime]
            .find(value => this._toValidDate(value) || this._formatTime(value));

        if (startTime === undefined) return '';

        const startDate = this._toValidDate(startTime);
        const endDate = this._toValidDate(endTime);
        const isMultiDay = startDate && endDate && startDate.toDateString() !== endDate.toDateString();

        if (isMultiDay) {
            const startDateLabel = inZone(startTime, { month: 'long', day: 'numeric' });
            const endDateLabel = inZone(endTime, { month: 'long', day: 'numeric', year: 'numeric' });
            return `${startDateLabel} - ${endDateLabel}`;
        }

        const dateLabel = this._formatDate(startTime);
        const startTimeLabel = timezone ? this._formatTimeInZone(startTime, timezone) : this._formatTime(startTime);
        const endTimeLabel = timezone ? this._formatTimeInZone(endTime, timezone) : this._formatTime(endTime);
        const timeLabel = startTimeLabel && endTimeLabel
            ? `${startTimeLabel} - ${endTimeLabel}`
            : startTimeLabel;

        return [dateLabel, timeLabel].filter(Boolean).join(' • ');
    }

    /**
     * Format location for display
     * @param {Array} locations - Array of location objects
     * @returns {string} Formatted location string
     */
    static _formatLocation(locations) {
        if (!locations || locations.length === 0) return '';

        return locations.map(loc => {
            const parts = [];
            if (loc.name) parts.push(loc.name);
            if (loc.detailedArea && loc.detailedArea !== loc.name) parts.push(loc.detailedArea);
            return parts.join(' - ');
        }).join('; ');
    }

    /**
     * Generate HTML content for the PDF
     * @param {Object} eventInstance - Event instance data
     * @param {Array} sortedTasks - Sorted service flow tasks
     * @returns {string} HTML content
     */
   static _generateHTML(eventInstance, sortedTasks, options = {}) {
    const eventName = eventInstance?.nameOverride || eventInstance?.Event?.name || 'Untitled Event';
    const dateTime = this._formatDateTime(eventInstance, options?.timezone || null);
    const location = options.locationName || this._formatLocation(eventInstance?.Locations);
    const notes = eventInstance?.notes || eventInstance?.Event?.defaultNotes || '';

    const organizationName = options?.organizationName || '';
    const organizationLogoDataUri = options?.organizationLogoDataUri || null;
    const assigneeAvatarDataByPersonId = options?.assigneeAvatarDataByPersonId || {};

    // Warm-neutral palette matching the app's light theme + module colors:
    // events orange accent, tasks amber, surface/borderSubtle neutrals.
    const ACCENT = '#E65F00';
    const INK = '#172033';
    const MUTED = '#5B6370';
    const FAINT = '#8791A2';
    const CARD_BG = '#FAFAF8';
    const CARD_EDGE = '#EAE7E1';

    return `
<!DOCTYPE html>
<html lang="en">
<head>
<meta charset="utf-8" />
<meta name="color-scheme" content="light" />
<style>
@page {
    size: letter portrait;
    margin: 8mm;
}

* {
    box-sizing: border-box;
}

html,
body {
    margin: 0;
    color-scheme: light;
}

body {
    font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Inter, Helvetica, Arial, sans-serif;
    background: #ffffff;
    color: #172033;
    font-size: 12px;
    line-height: 1.3;
    -webkit-print-color-adjust: exact;
    print-color-adjust: exact;
}

/* ===== Header ===== */

.header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    min-height: 32px;
    margin-bottom: 8px;
}

.logo {
    display: flex;
    align-items: center;
    min-width: 0;
}

.logo img {
    display: block;
    width: auto;
    max-width: 150px;
    height: 30px;
    object-fit: contain;
    object-position: left center;
}

.logo-fallback {
    width: 30px;
    height: 30px;
    border-radius: 7px;
    background: ${INK};
    color: #ffffff;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 11px;
    font-weight: 800;
    letter-spacing: 0.04em;
}

.document-label {
    color: #687386;
    font-size: 9px;
    font-weight: 700;
    letter-spacing: 0.14em;
    text-transform: uppercase;
}

.hero {
    position: relative;
    overflow: hidden;
    margin-bottom: 10px;
    padding: 13px 16px 12px;
    border: 1px solid ${CARD_EDGE};
    border-left: 4px solid ${ACCENT};
    border-radius: 8px;
    background: ${CARD_BG};
    color: #172033;
    page-break-inside: avoid;
    break-inside: avoid;
}

.hero::after {
    content: "";
    position: absolute;
    top: -38px;
    right: -20px;
    width: 76px;
    height: 76px;
    border: 10px solid #f1ece3;
    border-radius: 999px;
}

.hero > * {
    position: relative;
    z-index: 1;
}

.event-kicker {
    margin-bottom: 3px;
    color: ${ACCENT};
    font-size: 9px;
    font-weight: 800;
    letter-spacing: 0.16em;
    text-transform: uppercase;
}

.event-name {
    margin: 0;
    color: #111a2c;
    font-size: 21px;
    font-weight: 800;
    line-height: 1.06;
    letter-spacing: -0.025em;
}

.event-meta-list {
    display: flex;
    flex-wrap: wrap;
    gap: 3px 12px;
    margin-top: 7px;
}

.event-meta {
    color: #364158;
    font-size: 11px;
    font-weight: 550;
}

.event-meta-label {
    margin-right: 4px;
    color: ${FAINT};
    font-size: 9px;
    font-weight: 800;
    letter-spacing: 0.08em;
    text-transform: uppercase;
}

/* ===== Notes ===== */

.notes {
    margin: 9px 0 15px;
    color: #39445a;
    font-size: 11px;
    line-height: 1.3;
    page-break-inside: avoid;
    break-inside: avoid;
}

.notes-title {
    margin-bottom: 1px;
    color: ${MUTED};
    font-size: 10px;
    font-weight: 800;
    letter-spacing: 0.09em;
    text-transform: uppercase;
}

.notes-copy {
    white-space: pre-wrap;
}

/* ===== Section Title ===== */

.section-heading {
    display: flex;
    align-items: baseline;
    justify-content: space-between;
    margin-bottom: 5px;
    page-break-after: avoid;
    break-after: avoid;
}

.section-title {
    color: #172033;
    font-size: 12px;
    font-weight: 750;
}

.section-count {
    color: #8791a2;
    font-size: 11px;
    font-weight: 650;
}

/* ===== Flow list (mirrors the in-app timeline) ===== */

.flow-list {
    list-style: none;
    padding: 0;
    margin: 0;
}

.flow-item {
    display: grid;
    grid-template-columns: 13px minmax(0, 1fr);
    gap: 10px;
    padding: 5px 0 11px;
    page-break-inside: avoid;
    break-inside: avoid;
}

.flow-rail {
    position: relative;
}

.flow-rail::before {
    content: "";
    position: absolute;
    top: 0;
    bottom: 0;
    left: 6px;
    width: 1px;
    background: #e3e6ea;
}

/* Rail never runs above/below the end markers. */
.flow-item:first-child .flow-rail::before {
    top: 18px;
}

.flow-item:last-child .flow-rail::before {
    bottom: calc(100% - 18px);
}

/* Uniform eventsContainer circle cue — same marker for every item type;
   the chip carries type identity, matching the in-app row. */
.flow-marker {
    position: absolute;
    top: 8px;
    left: 1.5px;
    width: 10px;
    height: 10px;
    border-radius: 999px;
    background: #fed7aa;
}

.flow-header {
    display: flex;
    align-items: center;
    gap: 8px;
    padding-top: 2px;
}

.flow-title {
    flex: 1;
    min-width: 0;
    color: ${INK};
    font-size: 12.5px;
    font-weight: 750;
    line-height: 1.25;
}

.flow-chip {
    flex-shrink: 0;
    padding: 2px 8px;
    border-radius: 999px;
    font-size: 8.5px;
    font-weight: 750;
    letter-spacing: 0.05em;
}

.chip-agenda {
    background: #eceef1;
    color: #5b6370;
}

.chip-task {
    background: #fde68a;
    color: #92400e;
}

.chip-worship {
    background: #fed7aa;
    color: #c2410c;
}

/* Details card — CutCard shape: clipped top-right + bottom-left corners,
   flat surface, no border. Rendered only when the item actually has info. */
.flow-details {
    margin-top: 4px;
    padding: 8px 11px;
    background: ${CARD_BG};
    clip-path: polygon(0 0, calc(100% - 12px) 0, 100% 12px, 100% 100%, 10px 100%, 0 calc(100% - 10px));
}

.flow-details > * + * {
    margin-top: 5px;
}

.flow-meta {
    color: ${MUTED};
    font-size: 10.5px;
    font-weight: 550;
}

.flow-meta .flow-time {
    color: ${INK};
    font-weight: 800;
    font-variant-numeric: tabular-nums;
}

.flow-assignee {
    display: flex;
    align-items: center;
    gap: 6px;
    color: #687386;
    font-size: 11px;
    line-height: 1.2;
}

.flow-notes-heading {
    color: ${FAINT};
    font-size: 8.5px;
    font-weight: 800;
    letter-spacing: 0.09em;
    text-transform: uppercase;
}

.flow-notes {
    color: #4b566a;
    font-size: 11px;
    line-height: 1.3;
    white-space: pre-wrap;
}

/* Rich (TipTap HTML) notes render as markup, not escaped text. */
.flow-notes-rich {
    color: #4b566a;
    font-size: 11px;
    line-height: 1.3;
}

.flow-notes-rich p {
    margin: 0 0 5px;
}

.flow-notes-rich p:last-child {
    margin-bottom: 0;
}

.flow-notes-rich ul,
.flow-notes-rich ol {
    margin: 3px 0;
    padding-left: 18px;
}

.flow-notes-rich blockquote {
    margin: 3px 0;
    padding-left: 10px;
    border-left: 2px solid #e3e6ea;
    color: #687386;
}

.assignee-avatars {
    display: flex;
    align-items: center;
    flex-shrink: 0;
}

.assignee-avatar,
.assignee-fallback {
    width: 30px;
    height: 30px;
    border: 2px solid #ffffff;
    border-radius: 999px;
}

.assignee-avatar {
    object-fit: cover;
}

.assignee-fallback {
    display: flex;
    align-items: center;
    justify-content: center;
    background: #e8edf5;
    color: #3d485d;
    font-size: 9px;
    font-weight: 800;
}

.assignee-avatar + .assignee-avatar,
.assignee-avatar + .assignee-fallback,
.assignee-fallback + .assignee-avatar,
.assignee-fallback + .assignee-fallback {
    margin-left: -6px;
}

.assignee-names {
    color: #687386;
}

.unassigned {
    color: #9aa3b2;
    font-style: italic;
}

/* ===== Empty State ===== */

.empty-state {
    padding: 16px;
    border-radius: 7px;
    background: #f4f7fb;
    color: #687386;
    text-align: center;
    font-size: 11px;
}

/* ===== Footer ===== */

.footer {
    margin-top: 10px;
    padding-top: 5px;
    border-top: 1px solid #e3e8f0;
    color: #9aa3b2;
    font-size: 9px;
    text-align: center;
    page-break-inside: avoid;
    break-inside: avoid;
}

/* ===== Print ===== */


</style>
</head>

<body>

<!-- Header -->
<div class="header">
    <div class="logo">
        ${organizationLogoDataUri
            ? `<img src="${organizationLogoDataUri}" alt="${this._escapeHtml(organizationName || 'Organization')}" />`
            : `<div class="logo-fallback">${this._escapeHtml((organizationName || 'ORG').split(' ').map(w => w[0]).join('').slice(0, 4).toUpperCase())}</div>`}
    </div>
    <div class="document-label">Service Flow</div>
</div>

<div class="hero">
    <div class="event-kicker">Order of Service</div>
    <h1 class="event-name">${this._escapeHtml(eventName)}</h1>
    ${(dateTime || location) ? `
    <div class="event-meta-list">
        ${dateTime ? `<div class="event-meta"><span class="event-meta-label">When</span>${this._escapeHtml(dateTime)}</div>` : ''}
        ${location ? `<div class="event-meta"><span class="event-meta-label">Where</span>${this._escapeHtml(location)}</div>` : ''}
    </div>
    ` : ''}
</div>

<!-- Notes -->
${notes ? `
<div class="notes">
    <div class="notes-title">Service Notes</div>
    <div class="notes-copy">${this._escapeHtml(notes)}</div>
</div>
` : ''}

<!-- Order of Service -->
<div class="section-heading">
    <div class="section-title">Service Flow</div>
    <div class="section-count">${sortedTasks.length} item${sortedTasks.length === 1 ? '' : 's'}</div>
</div>

${sortedTasks.length > 0 ? `
<ul class="flow-list">
${sortedTasks.map(task => {
    const timeLabel = this._formatItemTime(task, eventInstance, options?.timezone || null);
    const hasDuration = task.durationMinutes !== null && task.durationMinutes !== undefined && task.durationMinutes !== '';
    const durationLabel = hasDuration ? formatDurationLabel(task.durationMinutes) : '';
    // Meta line mirrors the app: "9:00 AM · 15 min" — time only for showTime
    // items, nothing invented when fields are empty.
    const metaInner = [
        timeLabel ? `<span class="flow-time">${this._escapeHtml(timeLabel)}</span>` : '',
        this._escapeHtml(durationLabel),
    ].filter(Boolean).join(' · ');
    const chipClass = task.type === 'TASK' ? 'chip-task' : task.type === 'WORSHIP' ? 'chip-worship' : 'chip-agenda';
    const chipLabel = task.type === 'TASK' ? 'Task' : task.type === 'WORSHIP' ? 'Worship set' : 'Agenda';
    // Details card renders only when the item actually has info.
    const detailsInner = [
        metaInner ? `<div class="flow-meta">${metaInner}</div>` : '',
        task.type === 'TASK' ? `
            <div class="flow-assignee">${task.taskRestricted
                ? '<span class="unassigned">Restricted</span>'
                : this._renderAssigneeRow(task.Assignees, assigneeAvatarDataByPersonId)}</div>` : '',
        task.description ? `
            <div>
                <div class="flow-notes-heading">Additional Notes</div>
                ${looksLikeHtml(task.description)
                    ? `<div class="flow-notes-rich">${task.description}</div>`
                    : `<div class="flow-notes">${this._escapeHtml(task.description)}</div>`}
            </div>` : '',
    ].filter(Boolean).join('');
    return `
    <li class="flow-item">
        <div class="flow-rail"><span class="flow-marker"></span></div>
        <div class="flow-main">
            <div class="flow-header">
                <div class="flow-title">${this._escapeHtml(task.title || 'Untitled Item')}</div>
                <span class="flow-chip ${chipClass}">${this._escapeHtml(chipLabel)}</span>
            </div>
            ${detailsInner ? `<div class="flow-details">${detailsInner}</div>` : ''}
        </div>
    </li>
`;
}).join('')}
</ul>
` : `
<div class="empty-state">
    No service flow items yet. Add items from the event's Service Flow tab.
</div>
`}

<!-- Footer -->
<div class="footer">
    Generated on ${new Date().toLocaleString('en-US', {
        weekday: 'long',
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
        hour12: true
    })}
</div>

</body>
</html>
`;
}

    static _renderAssigneeRow(assignees, assigneeAvatarDataByPersonId) {
        const list = (assignees || [])
            .slice()
            .sort((a, b) => (a.position || 0) - (b.position || 0));

        if (list.length === 0) {
            return '<span class="unassigned">Unassigned</span>';
        }

        const maxAvatars = 6;
        const avatarItems = list.slice(0, maxAvatars);
        const remainingCount = list.length - avatarItems.length;

        const avatarsHtml = avatarItems.map(a => {
            const dataUri = a?.id ? assigneeAvatarDataByPersonId?.[a.id] : null;
            if (dataUri) {
                return `<img class="assignee-avatar" src="${dataUri}" alt="${this._escapeHtml(`${a.firstName || ''} ${a.lastName || ''}`.trim())}" />`;
            }

            const initials = `${(a?.firstName || '').slice(0, 1)}${(a?.lastName || '').slice(0, 1)}`.toUpperCase() || '??';
            return `<div class="assignee-fallback">${this._escapeHtml(initials)}</div>`;
        }).join('');

        const remainingHtml = remainingCount > 0
            ? `<div class="assignee-fallback">+${remainingCount}</div>`
            : '';

        const names = list
            .map(a => `${a.firstName || ''} ${a.lastName || ''}`.trim())
            .filter(Boolean)
            .join(', ');

        return `
            <span class="assignee-avatars">${avatarsHtml}${remainingHtml}</span>
            <span class="assignee-names">${this._escapeHtml(names)}</span>
        `;
    }


    /**
     * Escape HTML special characters
     * @param {string} text - Text to escape
     * @returns {string} Escaped text
     */
    static _escapeHtml(text) {
        if (!text) return '';
        const div = { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#039;' };
        return String(text).replace(/[&<>"']/g, s => div[s]);
    }

    /**
     * Export to PDF on web platform
     * @private
     */
    static async _exportToPDFWeb(htmlContent, _options) {
        
        // Create a hidden iframe to render and print
        const iframe = document.createElement('iframe');
        iframe.style.position = 'absolute';
        iframe.style.width = '0';
        iframe.style.height = '0';
        iframe.style.border = 'none';
        document.body.appendChild(iframe);
        
        const iframeDoc = iframe.contentDocument || iframe.contentWindow.document;
        iframeDoc.open();
        iframeDoc.write(htmlContent);
        iframeDoc.close();
        
        // Wait for content to load, then print
        await new Promise(resolve => setTimeout(resolve, 500));
        
        iframe.contentWindow.print();
        
        // Clean up after print dialog closes
        setTimeout(() => {
            document.body.removeChild(iframe);
        }, 1000);
        
        return true;
    }

    /**
     * Export to PDF on native platforms
     * @private
     */
    static async _exportToPDFNative(htmlContent, eventInstance, _options) {
        const RNHTMLtoPDF = await import('react-native-html-to-pdf');
        
        const eventName = eventInstance?.nameOverride || eventInstance?.Event?.name || 'Service-Flow';
        const safeFilename = `${eventName}-${_options?.locationName || ''}`.replace(/[^a-zA-Z0-9]/g, '-').replace(/-+/g, '-');
        
        const pdfOptions = {
            html: htmlContent,
            fileName: `${safeFilename}-Service-Flow`,
            directory: 'Documents',
            base64: false,
            bgColor: '#ffffff',
            shouldPrintBackgrounds: true
        };
        
        const file = await RNHTMLtoPDF.generatePDF(pdfOptions);
        
        if (await Sharing.isAvailableAsync()) {
            await Sharing.shareAsync(file.filePath, {
                mimeType: 'application/pdf',
                dialogTitle: 'Save Service Flow PDF'
            });
        }
        
        return true;
    }
}

export default ServiceFlowExporter;
